<template>
  <tabs-wrapper :tabs="tabs">
    <template #[tabs.at(0).title]>
      <div class="rbz-field cert-detach-help-text" v-if="actionDetachCertificate">
        {{ detachActionText }}
      </div>
      <div class="rbz-field">
        <rbz-dropdown
          :items="allItems.map((item) => [item.id, item.name])"
          :value="selectedItems.map((item) => item.id)"
          @update:value="updateSelectedItems($event)"
          @is-open="fixModalDropdownScroll($event, $refs.rbzDropdownRef)"
          type="multi-select"
          ref="rbzDropdownRef"
          :label="t('certificate.appsCounter', { count: selectedItems.length })"
          class="apps-selection"
          data-qa="apps-selection"
        />
      </div>
    </template>
    <template #[tabs.at(1).title]>
      <div class="rbz-field">
        <rbz-dropdown
          :items="assignedCertificatesExceptCurrent.map((item) => [item.id, item.name])"
          v-model:value="replaceCertificate"
          @update:value="updateReplaceCertificate($event)"
          @is-open="fixModalDropdownScroll($event, $refs.rbzDropdownRef)"
          ref="rbzDropdownRef"
          :label="t('certificate.singular')"
          class="replace-certificate-selection"
          data-qa="replace-certificate-selection"
        />
      </div>
    </template>
  </tabs-wrapper>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { BackendService, Certificate, ServerGroup, Tab } from '@/types';
import TabsWrapper from 'ui-component-library/rbz/components/TabsWrapper.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';

interface CertificatesTabs {
  detachActionText?: string;
  allItems: ServerGroup[] | BackendService[];
  selectedItems: ServerGroup[] | BackendService[];
  assignedCertificates: Certificate[];
  selectedCertificate: Certificate;
  side: Certificate['side'];
}

const replaceCertificate = ref<Certificate['id']>(null);

const props = defineProps<CertificatesTabs>();

interface CertificatesTabsEmits {
  (e: 'update-selected-apps', newAppsIds: ServerGroup['id'][] | BackendService['id'][]): void;

  (e: 'update-replace-certificate', id: Certificate['id']): void;
}

const emit = defineEmits<CertificatesTabsEmits>();
const { t } = useI18n();
const actionDetachCertificate = ref(false);

const tabs = computed<Tab[]>(() => {
  let newAttachmentTab = '';
  switch (props.side) {
    case 'server': {
      newAttachmentTab = t('certificate.newAttachmentApplication');
      break;
    }
    case 'clientCA': {
      newAttachmentTab = t('certificate.newAttachmentApplication');
      break;
    }
    case 'serverToBackendMTLS': {
      newAttachmentTab = t('certificate.newAttachmentBackendService');
      break;
    }
    case 'backendCA': {
      newAttachmentTab = t('certificate.newAttachmentBackendService');
      break;
    }
  }
  return [
    {
      title: newAttachmentTab,
    },
    {
      title: t('certificate.replaceExistingCertificate'),
    },
  ];
});

const updateSelectedItems = (newAppsIds: ServerGroup['id'][] | BackendService['id'][]) => {
  if (props.selectedItems && props.selectedItems.length > newAppsIds.length) {
    actionDetachCertificate.value = true;
  }
  emit('update-selected-apps', newAppsIds);
};

const updateReplaceCertificate = (id: Certificate['id']) => {
  emit('update-replace-certificate', id);
};

function fixModalDropdownScroll(isOpen: boolean, dropdownRef: InstanceType<typeof RbzDropdown>) {
  if (isOpen) {
    setTimeout(() => {
      dropdownRef.dropdownListRef?.scrollIntoView();
    }, 0);
  }
}

const assignedCertificatesExceptCurrent = computed((): Certificate[] => {
  return props.assignedCertificates.filter((certificate: Certificate) => {
    return certificate.name !== props.selectedCertificate.name;
  });
});
</script>
<style>
.cert-detach-help-text {
  font-size: 12px;
}
</style>
